import z from 'zod';

export const RESEARCH_ONTOLOGY_VERSION = '2026.3.0';
export const RESEARCH_ONTOLOGY_REVIEW_DATE = '2026-09-01';

export const RESEARCH_DOMAIN_IDS = [
    'financial-reporting-performance',
    'capital-structure-liquidity-valuation',
    'portfolio-investment-analytics',
    'operating-metrics-unit-economics',
    'commercial-customers-pricing',
    'market-industry-competition',
    'macroeconomics-rates-policy',
    'strategy-growth-outlook',
    'products-technology-intellectual-property',
    'operations-assets-supply-chain',
    'governance-ownership-leadership',
    'legal-regulatory-compliance',
    'risk-controls-resilience',
    'human-capital-organization',
    'sustainability-climate-natural-resources',
    'transactions-contracts-commitments',
] as const;

export type ResearchDomainId = (typeof RESEARCH_DOMAIN_IDS)[number];

export const AUTHORITATIVE_SCHEME_IDS = [
    'us-gaap',
    'sec-dei',
    'sec-srt',
    'sec-cyd',
    'sec-ecd',
    'sec-fnd',
    'fred',
    'bea',
    'bls',
    'census-naics',
    'census-napcs',
    'fibo',
    'finos-cdm',
] as const;

export type AuthoritativeSchemeId = (typeof AUTHORITATIVE_SCHEME_IDS)[number];

export const CONCEPT_MAPPING_RELATIONS = [
    'exactMatch',
    'closeMatch',
    'broadMatch',
    'narrowMatch',
    'relatedMatch',
] as const;

export type ConceptMappingRelation = (typeof CONCEPT_MAPPING_RELATIONS)[number];

export const CONCEPT_VALUE_KINDS = [
    'currency',
    'duration',
    'integer',
    'narrative',
    'percentage',
    'ratio',
    'series',
] as const;

export type ConceptValueKind = (typeof CONCEPT_VALUE_KINDS)[number];

export interface ResearchDomainDefinition {
    readonly description: string;
    readonly id: ResearchDomainId;
    readonly label: string;
    readonly order: number;
}

export interface AuthoritativeConceptScheme {
    readonly documentationUrl: string;
    readonly governance: {
        readonly commercialUse: 'permitted-no-fee';
        readonly licenseId: string;
        readonly licenseUrl: string;
        readonly mappingMode: 'external-identifiers-only';
        readonly useStatus: 'enabled';
    };
    readonly id: AuthoritativeSchemeId;
    readonly label: string;
    readonly publisher: string;
    readonly sourceType?: 'live-api' | 'versioned-taxonomy';
    readonly version: string;
}

export interface AuthoritativeConceptMapping {
    readonly externalId: string;
    readonly relation: ConceptMappingRelation;
    readonly reviewedAt: string;
    readonly schemeId: AuthoritativeSchemeId;
    readonly schemeVersion: string;
    readonly verification?: {
        readonly status: 'declared' | 'source-verified' | 'stale' | 'unavailable';
        readonly verifiedAt?: string;
    };
}

export interface ResearchConceptDefinition {
    readonly aliases: readonly string[];
    readonly description: string;
    readonly id: string;
    readonly mappings: readonly AuthoritativeConceptMapping[];
    readonly patterns: readonly string[];
    readonly preferredLabel: string;
    readonly primaryDomainId: ResearchDomainId;
    readonly priority: number;
    readonly reviewedAt: string;
    readonly secondaryDomainIds: readonly ResearchDomainId[];
    readonly status: 'active' | 'deprecated';
    readonly valueKinds: readonly ConceptValueKind[];
    readonly version: string;
}

export const ResearchDomainDefinitionSchema = z.object({
    description: z.string().min(1),
    id: z.enum(RESEARCH_DOMAIN_IDS),
    label: z.string().min(1),
    order: z.number().int().positive(),
});

export const AuthoritativeConceptSchemeSchema = z.object({
    documentationUrl: z.string().url(),
    governance: z.object({
        commercialUse: z.literal('permitted-no-fee'),
        licenseId: z.string().min(1),
        licenseUrl: z.string().url(),
        mappingMode: z.literal('external-identifiers-only'),
        useStatus: z.literal('enabled'),
    }),
    id: z.enum(AUTHORITATIVE_SCHEME_IDS),
    label: z.string().min(1),
    publisher: z.string().min(1),
    sourceType: z.enum(['live-api', 'versioned-taxonomy']).optional(),
    version: z.string().min(1),
});

export const AuthoritativeConceptMappingSchema = z.object({
    externalId: z.string().min(1),
    relation: z.enum(CONCEPT_MAPPING_RELATIONS),
    reviewedAt: z.iso.date(),
    schemeId: z.enum(AUTHORITATIVE_SCHEME_IDS),
    schemeVersion: z.string().min(1),
    verification: z
        .object({
            status: z.enum(['declared', 'source-verified', 'stale', 'unavailable']),
            verifiedAt: z.iso.datetime().optional(),
        })
        .optional(),
});

export const ResearchConceptDefinitionSchema = z.object({
    aliases: z.array(z.string().min(1)),
    description: z.string().min(1),
    id: z.string().regex(/^[a-z0-9]+(?:[.-][a-z0-9]+)*$/),
    mappings: z.array(AuthoritativeConceptMappingSchema),
    patterns: z.array(z.string().min(2)).min(1),
    preferredLabel: z.string().min(1),
    primaryDomainId: z.enum(RESEARCH_DOMAIN_IDS),
    priority: z.number().int().min(1).max(10),
    reviewedAt: z.iso.date(),
    secondaryDomainIds: z.array(z.enum(RESEARCH_DOMAIN_IDS)),
    status: z.enum(['active', 'deprecated']),
    valueKinds: z.array(z.enum(CONCEPT_VALUE_KINDS)).min(1),
    version: z.string().min(1),
});
